import { estagioPelaFicha, fichaEnviada, PARTNER_TYPES, TIPOS_ESTABELECIMENTO } from "../constants";

// Junta parceiro e ficha na MESMA empresa.
//
// "Parceiros (Rede)" e "Fichas / Credenciamento" eram duas listas do mesmo
// estabelecimento, e as duas discordavam: parceiro em "abordagem" com ficha já
// concluída, ficha pendente de empresa que estava ativa na rede. Quem estava na
// rua abria uma tela para ver o estágio e outra para ver o documento.
//
// A chave é `partnerId` quando existe e o CNPJ quando não existe — é o que os
// dois lados sempre têm. Empresa que entrou pelo link (ficha sem parceiro)
// continua aparecendo: sumir justamente quem se cadastrou sozinho seria o
// oposto do que a unificação serve.

export const soDigitos = (v) => String(v || "").replace(/\D/g, "");

/**
 * O motivo da recusa, lido da trilha.
 *
 * O campo `sem_interesse_motivo` NÃO existe no banco: o motivo virou
 * obrigatório na tela, mas era descartado na gravação e sumia na primeira
 * recarga. A trilha (stage_history / historico) é coluna que existe, então é
 * lá que o motivo mora — e a leitura ainda aceita o campo solto, para o dia
 * em que a coluna for criada e para dados antigos.
 */
export function motivoDaRecusa(parceiro, ficha) {
  // Para no evento mais recente: se a empresa foi retomada depois da recusa,
  // o motivo antigo não vale mais — ele explica um "não" que já foi revisto.
  const naTrilha = [...(parceiro?.stageHistory || [])].reverse()
    .find((e) => e?.tipo === "sem_interesse" || e?.tipo === "retomada");
  if (naTrilha) return naTrilha.tipo === "sem_interesse" ? naTrilha.motivo || "" : "";
  const naFicha = [...(ficha?.historico || [])].reverse().find((e) => e?.acao === "sem_interesse" && e?.motivo);
  if (naFicha) return naFicha.motivo;
  return parceiro?.semInteresseMotivo || ficha?.semInteresseMotivo || "";
}

// "oficina" é o que o cadastro de parceiro grava quando NINGUÉM escolheu nada —
// é o primeiro item do select. Isso o torna indistinguível de uma oficina de
// verdade, e como ele vencia a ficha, todo card da Rede dizia "Oficina Mecânica",
// inclusive os postos. Aqui um valor escolhido vence o padrão, venha de onde vier.
const TIPO_PADRAO = "oficina";

export function tipoDaEmpresa(parceiro, ficha) {
  const doParceiro = parceiro?.type || "";
  const daFicha = ficha?.tipoEstabelecimento || "";
  if (doParceiro && doParceiro !== TIPO_PADRAO) return doParceiro;
  if (daFicha && daFicha !== TIPO_PADRAO) return daFicha;
  return doParceiro || daFicha || "";
}

// O parceiro e a ficha usam listas diferentes (a ficha tem guincho, peças
// pesadas, "outro"; o parceiro tem "autopeca" no singular). Procura nas duas em
// vez de escolher uma e perder metade dos rótulos.
export function rotuloTipo(id) {
  if (!id) return "";
  return PARTNER_TYPES.find((t) => t.id === id)?.label
      || TIPOS_ESTABELECIMENTO.find((t) => t.id === id)?.label
      || "";
}

// Quando o nome da empresa contradiz o tipo padrão.
//
// Não corrige nada: sugere. O nome não é prova — "56 Posto De Molas" é loja de
// molas, não posto de combustível, e trocar isso sozinho gravaria um erro no
// lugar de outro. Também não dá para tirar do CNPJ: dos 33 casos suspeitos hoje,
// 30 não têm CNPJ cadastrado. Então a tela marca e uma pessoa decide.
const PISTAS_NO_NOME = [
  { tipo: "borracharia", re: /borracharia|pneus?\b/i },
  { tipo: "funilaria", re: /funilaria|pintura/i },
  { tipo: "autopeca", re: /auto\s*pe[çc]as|autope[çc]as/i },
  { tipo: "eletrica", re: /el[ée]trica/i },
  { tipo: "posto", re: /(auto\s*)?posto(?!\s+de\s+molas)|combust[íi]ve/i },
];

export function tipoSugeridoPeloNome(nome, tipoAtual) {
  if (tipoAtual && tipoAtual !== TIPO_PADRAO) return null;
  const n = String(nome || "");
  if (!n) return null;
  return PISTAS_NO_NOME.find((p) => p.re.test(n))?.tipo || null;
}

// O tipo da ficha sai do que foi marcado. Antes era "oficina" fixo, e todo posto
// registrado só para Combustível aparecia na Rede como Oficina Mecânica.
// Só Combustível = posto. Combustível junto de outro sistema = posto só se o
// nome disser (Auto Posto...); senão fica o padrão, que a Rede trata como "não escolhido".
export function tipoDoRegistroRapido(sistemas, nome) {
  const sis = Array.isArray(sistemas) ? sistemas : [];
  if (!sis.includes("combustivel")) return "oficina";
  if (sis.every((s) => s === "combustivel")) return "posto";
  return tipoSugeridoPeloNome(nome, "oficina") === "posto" ? "posto" : "oficina";
}

/**
 * Conversa validada no WhatsApp: existe o export da plataforma nesta empresa.
 *
 * O CRM já aceitava três coisas com o mesmo peso visual — o contato só anotado,
 * o print e o export assinado da conversa. Só o último prova, sozinho, que a
 * conversa existiu, quando foi e o que foi dito, e é ele que a análise de
 * abordagem consegue ler. O selo separa esses dos demais, sem apagar ninguém:
 * quem não tem export continua na lista, apenas sem o selo.
 *
 * Procura dos dois lados, porque o export entra por dois caminhos: o registro
 * de contato (interação do parceiro) e o registro rápido (evento na trilha da
 * ficha, que é onde dado novo mora — não existe coluna para isso).
 */
export function conversaValidada(parceiro, ficha) {
  const interacoes = Array.isArray(parceiro?.interactions) ? parceiro.interactions : [];
  const daInteracao = [...interacoes].reverse()
    .find((i) => i?.comprovantePath && (i?.comprovanteTipo === "conversa" || i?.conversaResumo));
  if (daInteracao) {
    return {
      ok: true,
      quando: daInteracao.diaDaConversa || (daInteracao.contatoEm || "").slice(0, 10) || daInteracao.date || "",
      mensagens: daInteracao.conversaResumo?.total || 0,
      numero: daInteracao.conversaResumo?.numero || "",
      // Quem respondeu do lado da empresa, segundo o export (nome do contato
      // no WhatsApp) — e quem atendeu do nosso lado.
      pessoa: daInteracao.conversaResumo?.contato && daInteracao.conversaResumo.contato !== "—" ? daInteracao.conversaResumo.contato : "",
      atendente: daInteracao.conversaResumo?.atendente || "",
    };
  }
  const trilha = Array.isArray(ficha?.historico) ? ficha.historico : [];
  const naFicha = [...trilha].reverse()
    .find((h) => h?.comprovantePath && (h?.comprovanteTipo === "conversa" || h?.conversaResumo));
  if (naFicha) {
    return {
      ok: true,
      quando: (naFicha.contatoEm || naFicha.ts || "").slice(0, 10),
      mensagens: naFicha.conversaResumo?.total || 0,
      numero: naFicha.conversaResumo?.numero || "",
      // Quem respondeu do lado da empresa, segundo o export (nome do contato
      // no WhatsApp) — e quem atendeu do nosso lado.
      pessoa: naFicha.conversaResumo?.contato && naFicha.conversaResumo.contato !== "—" ? naFicha.conversaResumo.contato : "",
      atendente: naFicha.conversaResumo?.atendente || "",
    };
  }
  return { ok: false };
}

/** Um item da rede: a empresa, com o que se sabe dela dos dois lados. */
export function montarItem(parceiro, ficha) {
  const estagio = parceiro?.stage || estagioPelaFicha(ficha);
  return {
    chave: parceiro?.id || `ficha:${ficha?.id}`,
    parceiro: parceiro || null,
    ficha: ficha || null,
    estagio,
    nome: parceiro?.name || ficha?.nomeFantasia || ficha?.razaoSocial || "Sem nome",
    cnpj: parceiro?.cnpj || ficha?.cnpj || "",
    tipo: tipoDaEmpresa(parceiro, ficha),
    contato: parceiro?.contact || ficha?.responsavelNome || "",
    telefone: parceiro?.phone || ficha?.celular || ficha?.telefone || "",
    uf: parceiro?.region || ficha?.estado || "",
    cidade: ficha?.cidade || parceiro?.cidade || parceiro?.address || "",
    ultimoContato: parceiro?.lastContact || null,
    motivoSemInteresse: motivoDaRecusa(parceiro, ficha),
    retomarEm: parceiro?.nextActionDate || ficha?.nextActionDate || "",
    criadoEm: parceiro?.createdAt || ficha?.createdAt || "",
    // Quem responde pela empresa. Sai do parceiro e, na falta dele, da ficha —
    // o card precisa dizer isso para "transferir" e "assumir" fazerem sentido.
    dono: parceiro?.userId || ficha?.userId || null,
    conversa: conversaValidada(parceiro, ficha),
    // "Documentação" quer dizer que o fornecedor MANDOU a ficha. Quem aceitou
    // e ainda não mandou continua sendo conversa: o próximo passo é cobrar o
    // envio do link, não conferir documento. Sem esta separação, o grupo "Em
    // documentação" misturava as duas coisas e ninguém sabia o que cobrar.
    aguardandoFicha: estagio === "documentacao" && !fichaEnviada(ficha),
  };
}

/** A lista da rede: um item por empresa, sem repetir ninguém. */
export function unificarRede(partners = [], credenciamentos = []) {
  const porParceiro = new Map();
  const porCnpj = new Map();
  for (const f of credenciamentos) {
    if (f?.partnerId && !porParceiro.has(f.partnerId)) porParceiro.set(f.partnerId, f);
    const d = soDigitos(f?.cnpj);
    if (d.length === 14 && !porCnpj.has(d)) porCnpj.set(d, f);
  }

  const usadas = new Set();
  const itens = (partners || []).map((p) => {
    const ficha = porParceiro.get(p.id) || porCnpj.get(soDigitos(p.cnpj)) || null;
    if (ficha) usadas.add(ficha.id);
    return montarItem(p, ficha);
  });

  for (const f of credenciamentos || []) {
    if (!usadas.has(f.id)) itens.push(montarItem(null, f));
  }
  return itens;
}

// ── Fichas duplicadas da mesma empresa ────────────────────────────
//
// O link de ficha NOVA mandado para quem já tinha ficha criava um segundo
// cadastro: o do registro rápido (vazio, com a prova do contato) e o que o
// fornecedor preencheu (completo, solto). A causa foi fechada nos dois lugares
// que geravam o link; aqui é para as duplicatas que JÁ existem, e para as que
// ainda escaparem: achar e juntar.
//
// Mesmo parceiro ou mesmo CNPJ é a mesma empresa. Telefone é pista forte, não
// prova (um contador atende várias oficinas) — por isso a junção é sempre um
// clique de alguém, nunca automática.
const telefones = (f) => [f?.celular, f?.telefone, f?.responsavelTelefone]
  .map(soDigitos).filter((d) => d.length >= 10).map((d) => d.slice(-8));

export function duplicatasDaFicha(ficha, todas = []) {
  if (!ficha) return [];
  const cnpj = soDigitos(ficha.cnpj);
  const tels = new Set(telefones(ficha));
  return todas.filter((f) => f && f.id !== ficha.id && f.status !== "sem_interesse" && (
    (ficha.partnerId && f.partnerId === ficha.partnerId)
    || (cnpj.length === 14 && soDigitos(f.cnpj) === cnpj)
    || telefones(f).some((t) => tels.has(t))
  ));
}

// ── Parceiro que JÁ existe para esta ficha ─────────────────────────
//
// "Trazer para o funil" e "registrar contato" num card que só tinha ficha
// criavam um parceiro novo sem olhar a rede. Caso de 23/10/2026: o "Nosso
// Postão" existia desde 07/10 (sem CNPJ, por isso a rede não o juntava à ficha
// do registro rápido) e o clique criou "Postao Auto Center" — duas vezes, porque
// a gravação do vínculo falhou (coluna gerada, ver colunasGeradas.js) e o card
// voltou sem parceiro. Resultado: a mesma empresa em 3 cards e a ficha
// preenchida pelo link pendurada numa cópia.
//
// Mesmo CNPJ é a mesma empresa: liga direto (`certo`). Mesmo telefone é pista
// forte, não prova (contador atende várias) — vem como `candidatos`, e quem
// clicou decide. Parceiro sem interesse não conta.
const telefonesDoParceiro = (p) => [p?.phone].map(soDigitos).filter((d) => d.length >= 10).map((d) => d.slice(-8));

export function parceiroExistenteDaFicha(ficha, partners = []) {
  if (!ficha) return { certo: null, candidatos: [] };
  const vivos = (partners || []).filter((p) => p && p.stage !== "sem_interesse");
  const cnpj = soDigitos(ficha.cnpj);
  if (cnpj.length === 14) {
    const mesmo = vivos.filter((p) => soDigitos(p.cnpj) === cnpj);
    if (mesmo.length === 1) return { certo: mesmo[0], candidatos: [] };
    if (mesmo.length > 1) return { certo: null, candidatos: mesmo };
  }
  const tels = new Set(telefones(ficha));
  const candidatos = tels.size ? vivos.filter((p) => telefonesDoParceiro(p).some((t) => tels.has(t))) : [];
  return { certo: null, candidatos };
}

// Qual das duas fica: a mais completa. Concluída vence assinada, que vence a do
// link, que vence a do registro rápido — a que sobra é a que tem menos a perder.
const pesoDaFicha = (f) => (f?.status === "concluido" ? 8 : 0) + (f?.aceiteTermos ? 4 : 0)
  + (f?.origem === "link" ? 2 : 0) + (soDigitos(f?.cnpj).length === 14 ? 1 : 0);

export function escolherPrincipal(a, b) {
  return pesoDaFicha(b) > pesoDaFicha(a) ? [b, a] : [a, b];
}

// Campos que a junção não copia: identidade da linha, o que só o servidor
// escreve, e os que têm regra própria logo abaixo.
const NAO_COPIAR = new Set(["id", "editToken", "createdAt", "historico", "documentos", "integracaoSync", "manutencaoSync", "status", "origem", "notes"]);
const vazio = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0)
  || (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

/** A ficha principal com o que só a outra tinha. Nada da principal é sobrescrito. */
export function mesclarFichas(principal, extra, autor = "") {
  const out = { ...principal };
  for (const [k, v] of Object.entries(extra || {})) {
    if (NAO_COPIAR.has(k)) continue;
    if (vazio(out[k]) && !vazio(v)) out[k] = v;
  }
  const docs = [...(principal.documentos || [])];
  for (const d of extra.documentos || []) if (d?.path && !docs.some((x) => x.path === d.path)) docs.push(d);
  out.documentos = docs;
  out.notes = [principal.notes, extra.notes].filter(Boolean).join(" | ") || null;
  const trilha = [...(principal.historico || []), ...(extra.historico || []).map((h) => ({ ...h, daFichaMesclada: extra.id }))]
    .sort((x, y) => String(x.ts || "").localeCompare(String(y.ts || "")));
  const origem = { link: "link", contato: "registro rápido", visita: "visita" }[extra.origem] || extra.origem || "ficha";
  trilha.push({
    ts: new Date().toISOString(), acao: "edicao", tipo: "mescla", autor, fichaMesclada: extra.id,
    detalhe: `Ficha duplicada juntada a esta: "${extra.nomeFantasia || extra.razaoSocial || "sem nome"}" (${origem}, ${extra.createdAt || "sem data"}). Histórico, documentos e contatos vieram junto.`,
  });
  out.historico = trilha;
  return out;
}
